import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxDropzoneChangeEvent } from 'ngx-dropzone';
import { SystemService } from '../../../services/system.service';
import { GlobalSearchService } from '../../../services/globalsearchservice.service';

@Component({
	selector: 'app-blog',
	templateUrl: './blog.component.html',
	styleUrls: ['./blog.component.scss']
})
export class BlogComponent implements OnInit {

	@ViewChild('postEdit') postEditRef: ElementRef;
	@ViewChild('confirmDeleteModal') confirmDeleteModalRef: ElementRef;

	posts: any = false;
	categories: any = [];
	postForm: UntypedFormGroup;
	selectedCategoryIds: any[] = [];
	pendingDeletePost: any = null;
	uploadedImages: any[] = [];
	uploadingImage = false;
	saving = false;

	statuses = [
		{ label: 'Published', value: true },
		{ label: 'Draft', value: false }
	];

	constructor(
		private formBuilder: UntypedFormBuilder,
		public systemService: SystemService,
		private globalSearchService: GlobalSearchService,
		private modalService: NgbModal
	) { }

	ngOnInit(): void {
		this.loadPosts();
		this.loadCategories();
	}

	loadPosts() {
		this.posts = false;
		this.systemService.getBlogPosts().subscribe((r: any) => {
			this.posts = r.posts || r || [];
		}, () => {
			this.posts = [];
			this.notify('Unable to load blog posts.', 'danger');
		});
	}

	loadCategories() {
		this.systemService.getBlogCategories().subscribe((r: any) => {
			this.categories = r.categories || r || [];
		}, () => {
			this.categories = [];
		});
	}

	createPost() {
		this.selectedCategoryIds = [];
		this.uploadedImages = [];

		this.postForm = this.formBuilder.group({
			blog_id: [''],
			title: ['', Validators.required],
			author: ['', Validators.required],
			image: [''],
			description: ['', Validators.required],
			meta_title: [''],
			meta_description: [''],
			meta_keyword: [''],
			allow_comments: [true],
			status: [false],
			sort_order: [0]
		});

		this.modalService.open(this.postEditRef, { ariaLabelledBy: 'modal-title', size: 'lg' }).result.then(() => { }, () => { });
	}

	editPost(post: any) {
		this.selectedCategoryIds = (post.categories || []).map((c: any) => c.blog_category_id || c);
		this.uploadedImages = [];

		this.postForm = this.formBuilder.group({
			blog_id: [post.blog_id],
			title: [post.title, Validators.required],
			author: [post.author, Validators.required],
			image: [post.image || ''],
			description: [post.description || '', Validators.required],
			meta_title: [post.meta_title || ''],
			meta_description: [post.meta_description || ''],
			meta_keyword: [post.meta_keyword || ''],
			allow_comments: [!!(+post.allow_comments)],
			status: [!!(+post.status)],
			sort_order: [post.sort_order || 0]
		});

		this.modalService.open(this.postEditRef, { ariaLabelledBy: 'modal-title', size: 'lg' }).result.then(() => { }, () => { });
	}

	isCategorySelected(categoryId: any): boolean {
		return this.selectedCategoryIds.indexOf(categoryId) !== -1;
	}

	toggleCategory(categoryId: any, event: any) {
		if (event.target.checked) {
			if (!this.isCategorySelected(categoryId)) {
				this.selectedCategoryIds.push(categoryId);
			}
		} else {
			this.selectedCategoryIds = this.selectedCategoryIds.filter(id => id !== categoryId);
		}
	}

	onDescriptionChange(html: string) {
		this.postForm.get('description').setValue(html);
	}

	onImageSelect(event: NgxDropzoneChangeEvent) {
		this.uploadedImages = event.addedFiles;

		const file = this.uploadedImages[0];

		if (!file) {
			return;
		}

		this.uploadingImage = true;

		this.readFile(file).then(fileContents => {
			const upload = { content: fileContents, name: file.name, mime: file.type };

			this.systemService.uploadBlogImage(upload, this.postForm.get('blog_id').value || 0).subscribe((result: any) => {
				this.postForm.get('image').setValue(result.file || result.image || '');
				this.uploadingImage = false;
			}, () => {
				this.uploadingImage = false;
				this.notify('Image upload failed.', 'danger');
			});
		});
	}

	onImageRemove() {
		this.uploadedImages = [];
		this.postForm.get('image').setValue('');
	}

	private async readFile(file: File): Promise<string | ArrayBuffer> {
		return new Promise<string | ArrayBuffer>((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = e => resolve((e.target as FileReader).result);
			reader.onerror = () => reject(null);
			if (!file) {
				return reject(null);
			}
			reader.readAsDataURL(file);
		});
	}

	savePost() {
		if (this.postForm.invalid) {
			this.postForm.markAllAsTouched();
			this.notify('Please fill in the required fields before saving.', 'danger');
			return;
		}

		this.saving = true;

		const data = { ...this.postForm.value, category_ids: this.selectedCategoryIds };

		this.systemService.saveBlogPost(data).subscribe(() => {
			this.saving = false;
			this.modalService.dismissAll();
			this.loadPosts();
			this.notify('Blog post saved.', 'success');
		}, () => {
			this.saving = false;
			this.notify('Unable to save the blog post.', 'danger');
		});
	}

	confirmDeletePost(post: any) {
		this.pendingDeletePost = post;
		this.modalService.open(this.confirmDeleteModalRef, { ariaLabelledBy: 'modal-title', size: 'md' });
	}

	handleConfirmDelete(modal: any) {
		if (this.pendingDeletePost) {
			const data = { blog_id: this.pendingDeletePost.blog_id };

			this.systemService.removeBlogPost(data).subscribe(() => {
				this.pendingDeletePost = null;
				this.loadPosts();
				this.notify('Blog post deleted.', 'success');
			}, () => {
				this.notify('Unable to delete the blog post.', 'danger');
			});

			modal.close();
		}
	}

	cancelDelete(modal: any) {
		this.pendingDeletePost = null;
		modal.dismiss();
	}

	private notify(message: string, type: string) {
		this.globalSearchService.showNotification(message, type, 'bottom', 'right');
	}
}
